import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FileUp, Shield, Monitor, Calendar, Upload,
  Plus, X, ChevronRight, ChevronLeft, HelpCircle, Headphones,
  Clock, Hand, RefreshCw, Lock, Mic, Speaker, Camera, Eye, EyeOff
} from 'lucide-react';
import { jobListings as mockJobs } from '../mockData';
import * as api from '../lib/api';

const STEPS = [
  { id: 'resume', label: 'Upload Resume', icon: FileUp },
  { id: 'work-auth', label: 'Work Authorization', icon: Shield },
  { id: 'interview', label: 'General Interview', icon: Monitor, core: true },
  { id: 'availability', label: 'Availability', icon: Calendar },
];

const ApplicationFlowPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.getJobById(jobId);
        setJob(data);
      } catch {
        const mock = mockJobs.find(j => j.id === jobId || j.job_id === jobId);
        setJob(mock || { title: 'Job Position', id: jobId });
      }
      setLoading(false);
    };
    load();
  }, [jobId]);

  const markComplete = (stepId) => {
    setCompletedSteps(prev => new Set([...prev, stepId]));
  };

  const progress = Math.round((completedSteps.size / STEPS.length) * 100);

  const goNext = () => {
    if (activeStep < STEPS.length - 1) setActiveStep(activeStep + 1);
  };
  const goBack = () => {
    if (activeStep > 0) setActiveStep(activeStep - 1);
  };

  if (loading) return <div className="flex items-center justify-center h-screen bg-[#F9FBF5] text-gray-400">Loading...</div>;

  return (
    <div className="flex flex-col h-screen bg-[#F9FBF5]" data-testid="application-flow-page">
      {/* Top Bar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 bg-white shrink-0">
        <div className="flex items-center gap-4">
          <button data-testid="go-back-btn" onClick={() => navigate('/explore')} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Go back
          </button>
          <button data-testid="view-listing-btn" onClick={() => navigate(`/explore?listingId=${jobId}`)} className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
            View listing
          </button>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <HelpCircle className="w-4 h-4" />
            FAQ
          </button>
          <button className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <Headphones className="w-4 h-4" />
            Contact support
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar */}
        <div className="w-72 shrink-0 border-r border-gray-200 bg-white flex flex-col">
          <div className="p-5">
            <h2 className="font-semibold text-gray-900 text-sm mb-1" data-testid="app-title">{job?.title || 'Application'}</h2>
            <p className="text-xs text-gray-500 mb-3">{completedSteps.size} of {STEPS.length} steps done</p>
            {/* Progress bar */}
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1 bg-gray-100 rounded-full">
                <div className="h-full bg-[#F1F5E6]0 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
              <span className="text-xs text-gray-400 shrink-0">{progress}%</span>
            </div>
          </div>

          <nav className="flex-1 px-3">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              const isActive = activeStep === i;
              const isDone = completedSteps.has(step.id);
              return (
                <button
                  key={step.id}
                  data-testid={`step-nav-${step.id}`}
                  onClick={() => setActiveStep(i)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg mb-0.5 transition-all ${
                    isActive ? 'bg-[#F1F5E6]' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                    isActive ? 'bg-[#E0E8D0] text-[#0A3900]' : isDone ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-400'
                  }`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex items-center gap-1.5 flex-1 min-w-0">
                    <span className={`text-sm truncate ${isActive ? 'text-[#0A3900] font-medium' : isDone ? 'text-gray-700' : 'text-gray-500'}`}>
                      {step.label}
                    </span>
                    {step.core && (
                      <span className="px-1 py-0.5 bg-gray-200 text-[8px] font-bold text-gray-500 rounded uppercase tracking-wider shrink-0">Core</span>
                    )}
                  </div>
                  <div className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${
                    isDone ? 'border-emerald-500 bg-emerald-500' : isActive ? 'border-[#0A3900]' : 'border-gray-300'
                  }`}>
                    {isDone && (
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Right Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto px-8 py-6">
            <div className="max-w-2xl">
              {activeStep === 0 && <ResumeStep onComplete={() => { markComplete('resume'); goNext(); }} />}
              {activeStep === 1 && <WorkAuthStep onComplete={() => { markComplete('work-auth'); goNext(); }} />}
              {activeStep === 2 && <InterviewStep onComplete={() => { markComplete('interview'); goNext(); }} job={job} />}
              {activeStep === 3 && <AvailabilityStep onComplete={() => { markComplete('availability'); }} />}
            </div>
          </div>

          {/* Bottom Nav */}
          <div className="flex items-center justify-between px-8 py-4 border-t border-gray-200 bg-white shrink-0">
            <button
              data-testid="step-back-btn"
              onClick={goBack}
              disabled={activeStep === 0}
              className={`flex items-center gap-1.5 text-sm font-medium ${activeStep === 0 ? 'text-gray-300' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
            {activeStep < STEPS.length - 1 && (
              <button
                data-testid="step-next-btn"
                onClick={goNext}
                className="flex items-center gap-1.5 px-5 py-2 bg-[#0A3900] text-white text-sm font-medium rounded-lg hover:bg-[#1A5C0A] transition-colors"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   STEP 1: UPLOAD RESUME (Duty AI exact)
   ═══════════════════════════════════════════════ */
const ResumeStep = ({ onComplete }) => {
  const [fileName, setFileName] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '', email: '', phone: '', phoneCode: '+1',
    linkedinUrl: '', noLinkedin: false, summary: '',
  });
  const [education, setEducation] = useState([{ school: '', degree: '', startYear: '', endYear: '', major: '', gpa: '' }]);
  const [experience, setExperience] = useState([{ company: '', role: '', startYear: '', endYear: '', city: '', country: '', description: '' }]);
  const [projects, setProjects] = useState([{ name: '', startYear: '', endYear: '', description: '' }]);
  const [publications, setPublications] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [awards, setAwards] = useState([]);
  const [profiles, setProfiles] = useState({ leetcode: '', github: '', linkedin: '', codeforces: '' });
  const [links, setLinks] = useState({ portfolio: '', others: [''] });
  const [skills, setSkills] = useState([]);
  const [skillInput, setSkillInput] = useState('');

  const handleFile = (file) => {
    if (file && (file.type === 'application/pdf' || file.name.endsWith('.pdf'))) {
      setFileName(file.name);
    }
  };
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); };

  const years = Array.from({ length: 30 }, (_, i) => 2026 - i);

  const updateField = (setter, index, field, value) => {
    setter(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const addSkill = () => {
    if (skillInput.trim() && !skills.includes(skillInput.trim())) {
      setSkills([...skills, skillInput.trim()]);
      setSkillInput('');
    }
  };

  return (
    <div data-testid="resume-step">
      <h2 className="text-xl font-bold text-gray-900 mb-2">Upload resume</h2>
      <p className="text-sm text-gray-500 mb-6">Please upload your resume to initiate the application process and help us evaluate your qualifications.</p>

      {/* Drop zone */}
      <div
        data-testid="resume-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => document.getElementById('resume-upload').click()}
        className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all mb-8 ${
          dragOver ? 'border-[#3f8033] bg-[#F1F5E6]' : fileName ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'
        }`}
      >
        <input id="resume-upload" type="file" accept=".pdf" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
        <Upload className={`w-8 h-8 mx-auto mb-3 ${fileName ? 'text-emerald-500' : 'text-gray-400'}`} />
        {fileName ? (
          <>
            <p className="font-medium text-gray-900">{fileName}</p>
            <p className="text-xs text-emerald-600 mt-1">Uploaded successfully</p>
          </>
        ) : (
          <>
            <p className="font-medium text-gray-700">Drop your resume here</p>
            <p className="text-sm text-gray-400">or browse files on your computer</p>
            <p className="text-xs text-gray-400 mt-2">Supports PDF up to 3MB</p>
          </>
        )}
      </div>

      {/* Personal Info */}
      <div className="space-y-4 mb-8">
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Full name" required value={formData.fullName} onChange={(v) => setFormData({ ...formData, fullName: v })} placeholder="Your full name" />
          <FormField label="Email" required value={formData.email} onChange={(v) => setFormData({ ...formData, email: v })} placeholder="your@email.com" type="email" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Phone *</label>
            <div className="flex gap-2">
              <select value={formData.phoneCode} onChange={(e) => setFormData({ ...formData, phoneCode: e.target.value })} className="w-20 h-10 px-2 border border-gray-200 rounded-lg text-sm bg-white">
                <option value="+1">+1</option><option value="+44">+44</option><option value="+91">+91</option><option value="+49">+49</option><option value="+61">+61</option><option value="+81">+81</option>
              </select>
              <input data-testid="phone-input" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="Phone number" className="flex-1 h-10 px-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#3f8033]" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">LinkedIn URL</label>
            {!formData.noLinkedin ? (
              <input value={formData.linkedinUrl} onChange={(e) => setFormData({ ...formData, linkedinUrl: e.target.value })} placeholder="https://linkedin.com/in/..." className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#3f8033]" />
            ) : (
              <div className="h-10 flex items-center text-sm text-gray-400">LinkedIn not provided</div>
            )}
            <label className="flex items-center gap-2 mt-1.5 cursor-pointer">
              <input type="checkbox" checked={formData.noLinkedin} onChange={(e) => setFormData({ ...formData, noLinkedin: e.target.checked })} className="w-3.5 h-3.5 rounded text-[#0A3900]" />
              <span className="text-xs text-gray-500">I don't have a LinkedIn</span>
            </label>
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1.5 block">Summary</label>
          <textarea value={formData.summary} onChange={(e) => setFormData({ ...formData, summary: e.target.value })} placeholder="Profile Summary" rows={3} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:border-[#3f8033]" />
        </div>
      </div>

      {/* Education */}
      <SectionTitle title="Education" />
      {education.map((edu, i) => (
        <div key={i} className="border border-gray-200 rounded-xl p-4 mb-3 relative">
          {education.length > 1 && <RemoveBtn onClick={() => setEducation(education.filter((_, idx) => idx !== i))} />}
          <div className="grid grid-cols-2 gap-3">
            <FormField label="School" value={edu.school} onChange={(v) => updateField(setEducation, i, 'school', v)} placeholder="Ex: Stanford University" />
            <FormField label="Degree" value={edu.degree} onChange={(v) => updateField(setEducation, i, 'degree', v)} placeholder="Ex: Bachelor of Science" />
            <YearSelect label="Start year" value={edu.startYear} onChange={(v) => updateField(setEducation, i, 'startYear', v)} years={years} />
            <YearSelect label="End year" value={edu.endYear} onChange={(v) => updateField(setEducation, i, 'endYear', v)} years={years} />
            <FormField label="Major" value={edu.major} onChange={(v) => updateField(setEducation, i, 'major', v)} placeholder="Ex: Computer Science" />
            <FormField label="GPA" value={edu.gpa} onChange={(v) => updateField(setEducation, i, 'gpa', v)} placeholder="Ex: 3.9" />
          </div>
        </div>
      ))}
      <AddBtn label="Add Education" onClick={() => setEducation([...education, { school: '', degree: '', startYear: '', endYear: '', major: '', gpa: '' }])} />

      {/* Work Experience */}
      <SectionTitle title="Work Experience" />
      {experience.map((exp, i) => (
        <div key={i} className="border border-gray-200 rounded-xl p-4 mb-3 relative">
          {experience.length > 1 && <RemoveBtn onClick={() => setExperience(experience.filter((_, idx) => idx !== i))} />}
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Company" value={exp.company} onChange={(v) => updateField(setExperience, i, 'company', v)} placeholder="Ex: Microsoft" />
            <FormField label="Role" value={exp.role} onChange={(v) => updateField(setExperience, i, 'role', v)} placeholder="Ex: Software Engineer" />
            <YearSelect label="Start year" value={exp.startYear} onChange={(v) => updateField(setExperience, i, 'startYear', v)} years={years} />
            <YearSelect label="End year" value={exp.endYear} onChange={(v) => updateField(setExperience, i, 'endYear', v)} years={years} />
            <FormField label="City" value={exp.city} onChange={(v) => updateField(setExperience, i, 'city', v)} placeholder="Ex: San Francisco" />
            <FormField label="Country" value={exp.country} onChange={(v) => updateField(setExperience, i, 'country', v)} placeholder="Ex: United States of America" />
          </div>
          <div className="mt-3">
            <label className="text-xs font-medium text-gray-600 mb-1 block">Description</label>
            <textarea value={exp.description} onChange={(e) => updateField(setExperience, i, 'description', e.target.value)} placeholder="Ex: At Microsoft, I collaborated with..." rows={3} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:border-[#3f8033]" />
          </div>
        </div>
      ))}
      <AddBtn label="Add Work Experience" onClick={() => setExperience([...experience, { company: '', role: '', startYear: '', endYear: '', city: '', country: '', description: '' }])} />

      {/* Projects */}
      <SectionTitle title="Projects" />
      {projects.map((proj, i) => (
        <div key={i} className="border border-gray-200 rounded-xl p-4 mb-3 relative">
          {projects.length > 1 && <RemoveBtn onClick={() => setProjects(projects.filter((_, idx) => idx !== i))} />}
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Project Name" value={proj.name} onChange={(v) => updateField(setProjects, i, 'name', v)} placeholder="Project name" />
            <div />
            <YearSelect label="Start year" value={proj.startYear} onChange={(v) => updateField(setProjects, i, 'startYear', v)} years={years} />
            <YearSelect label="End year" value={proj.endYear} onChange={(v) => updateField(setProjects, i, 'endYear', v)} years={years} />
          </div>
          <div className="mt-3">
            <label className="text-xs font-medium text-gray-600 mb-1 block">Description</label>
            <textarea value={proj.description} onChange={(e) => updateField(setProjects, i, 'description', e.target.value)} placeholder="Developed a chatbot to help..." rows={3} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:border-[#3f8033]" />
          </div>
        </div>
      ))}
      <AddBtn label="Add Project" onClick={() => setProjects([...projects, { name: '', startYear: '', endYear: '', description: '' }])} />

      {/* Publications */}
      <SectionTitle title="Publications" />
      {publications.map((pub, i) => (
        <div key={i} className="border border-gray-200 rounded-xl p-4 mb-3 relative">
          <RemoveBtn onClick={() => setPublications(publications.filter((_, idx) => idx !== i))} />
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Title" value={pub.title} onChange={(v) => updateField(setPublications, i, 'title', v)} placeholder="Publication title" />
            <FormField label="Publication" value={pub.publication} onChange={(v) => updateField(setPublications, i, 'publication', v)} placeholder="Publication" />
            <FormField label="Date of publication" value={pub.date} onChange={(v) => updateField(setPublications, i, 'date', v)} placeholder="DD/MM/YYYY" />
            <FormField label="Link" value={pub.link} onChange={(v) => updateField(setPublications, i, 'link', v)} placeholder="https://" />
          </div>
          <div className="mt-3">
            <FormField label="Authors" value={pub.authors} onChange={(v) => updateField(setPublications, i, 'authors', v)} placeholder="Enter author name and press Enter" />
          </div>
        </div>
      ))}
      <AddBtn label="Add Publication" onClick={() => setPublications([...publications, { title: '', publication: '', date: '', link: '', authors: '' }])} />

      {/* Certifications */}
      <SectionTitle title="Certifications" />
      {certifications.map((cert, i) => (
        <div key={i} className="flex items-center gap-2 mb-2">
          <input value={cert} onChange={(e) => setCertifications(certifications.map((c, idx) => idx === i ? e.target.value : c))} placeholder="Certification name" className="flex-1 h-10 px-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#3f8033]" />
          <button onClick={() => setCertifications(certifications.filter((_, idx) => idx !== i))} className="p-2 text-gray-400 hover:text-red-500"><X className="w-4 h-4" /></button>
        </div>
      ))}
      <AddBtn label="Add Certification" onClick={() => setCertifications([...certifications, ''])} />

      {/* Awards */}
      <SectionTitle title="Awards" />
      {awards.map((award, i) => (
        <div key={i} className="flex items-center gap-2 mb-2">
          <input value={award} onChange={(e) => setAwards(awards.map((a, idx) => idx === i ? e.target.value : a))} placeholder="Award name" className="flex-1 h-10 px-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#3f8033]" />
          <button onClick={() => setAwards(awards.filter((_, idx) => idx !== i))} className="p-2 text-gray-400 hover:text-red-500"><X className="w-4 h-4" /></button>
        </div>
      ))}
      <AddBtn label="Add Award" onClick={() => setAwards([...awards, ''])} />

      {/* Profiles */}
      <SectionTitle title="Profiles" />
      <div className="space-y-3 mb-6">
        {[
          { key: 'leetcode', label: 'leetcode username', icon: '{ }' },
          { key: 'github', label: 'github username', icon: 'GH' },
          { key: 'linkedin', label: 'linkedin username', icon: 'in' },
          { key: 'codeforces', label: 'codeforces username', icon: 'CF' },
        ].map(p => (
          <div key={p.key} className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500 shrink-0">{p.icon}</div>
            <input value={profiles[p.key]} onChange={(e) => setProfiles({ ...profiles, [p.key]: e.target.value })} placeholder={p.label} className="flex-1 h-10 px-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#3f8033]" />
          </div>
        ))}
      </div>

      {/* Links */}
      <SectionTitle title="Links" />
      <div className="space-y-3 mb-6">
        <FormField label="Portfolio URL" value={links.portfolio} onChange={(v) => setLinks({ ...links, portfolio: v })} placeholder="https://yourportfolio.com" />
        {links.others.map((link, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="flex-1"><FormField label={i === 0 ? "Other links" : ""} value={link} onChange={(v) => setLinks({ ...links, others: links.others.map((l, idx) => idx === i ? v : l) })} placeholder="https://example.com" /></div>
            {links.others.length > 1 && <button onClick={() => setLinks({ ...links, others: links.others.filter((_, idx) => idx !== i) })} className="p-2 text-gray-400 hover:text-red-500 mt-5"><X className="w-4 h-4" /></button>}
          </div>
        ))}
        <AddBtn label="Add more links" onClick={() => setLinks({ ...links, others: [...links.others, ''] })} />
      </div>

      {/* Skills */}
      <SectionTitle title="Skills" />
      <div className="mb-8">
        <div className="flex flex-wrap gap-2 mb-3">
          {skills.map((s, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#F1F5E6] text-[#0A3900] rounded-full text-sm font-medium">
              {s}
              <X className="w-3 h-3 cursor-pointer hover:text-[#0A3900]" onClick={() => setSkills(skills.filter((_, idx) => idx !== i))} />
            </span>
          ))}
        </div>
        <input
          data-testid="skill-input"
          value={skillInput}
          onChange={(e) => setSkillInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSkill(); } }}
          placeholder="Type a skill and press Enter"
          className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#3f8033]"
        />
      </div>

      <button data-testid="resume-save-btn" onClick={onComplete} className="w-full py-3 bg-[#0A3900] text-white rounded-lg font-semibold text-sm hover:bg-[#1A5C0A] transition-colors">
        Save & Continue
      </button>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   STEP 2: WORK AUTHORIZATION (Duty AI exact)
   ═══════════════════════════════════════════════ */
const WorkAuthStep = ({ onComplete }) => {
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [sameLocation, setSameLocation] = useState(true);
  const [dob, setDob] = useState('');
  const [confirmAuth, setConfirmAuth] = useState(false);
  const [agreeRemain, setAgreeRemain] = useState(false);
  const [signName, setSignName] = useState('');
  const [signDate, setSignDate] = useState(new Date().toISOString().split('T')[0]);

  const countries = ['India', 'United States', 'United Kingdom', 'Canada', 'Germany', 'France', 'Australia', 'Japan', 'Brazil', 'Other'];

  return (
    <div data-testid="work-auth-step">
      <h2 className="text-xl font-bold text-gray-900 mb-2">Work authorization</h2>
      <p className="text-sm text-gray-500 mb-6">Please ensure you meet local employment requirements for work authorization. Accurate information helps us process your application smoothly.</p>

      {/* Location of Residence */}
      <h3 className="text-base font-semibold text-gray-900 mb-4">Location of Residence</h3>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1.5 block">Country *</label>
          <select data-testid="country-select" value={country} onChange={(e) => setCountry(e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-[#3f8033]">
            <option value="">Select country</option>
            {countries.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <FormField label="State/Province/Region" required value={state} onChange={setState} placeholder="Enter state" />
        <FormField label="City" required value={city} onChange={setCity} placeholder="Enter city" />
        <FormField label="Postal Code" required value={postalCode} onChange={setPostalCode} placeholder="Enter postal code" />
      </div>

      <label className="flex items-center gap-2.5 mb-6 cursor-pointer">
        <input type="checkbox" checked={sameLocation} onChange={(e) => setSameLocation(e.target.checked)} className="w-4 h-4 rounded text-[#0A3900]" />
        <span className="text-sm text-gray-700">My location of residence and physical working location are the same.</span>
      </label>

      {/* Legal Attestation */}
      <h3 className="text-base font-semibold text-gray-900 mb-1">Legal Attestation</h3>
      <p className="text-sm text-gray-500 mb-4">Confirm your legally authorized work status</p>

      <div className="space-y-4 mb-6">
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1.5 block">Date of Birth (in MM/DD/YYYY) *</label>
          <input data-testid="dob-input" type="text" value={dob} onChange={(e) => setDob(e.target.value)} placeholder="MM/DD/YYYY" className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#3f8033]" />
        </div>

        <label className="flex items-start gap-2.5 cursor-pointer">
          <input type="checkbox" checked={confirmAuth} onChange={(e) => setConfirmAuth(e.target.checked)} className="w-4 h-4 rounded text-[#0A3900] mt-0.5" />
          <span className="text-sm text-gray-700">I confirm that I am legally authorized to work from {country || 'my country of residence'}. *</span>
        </label>
        <p className="text-xs text-gray-400 ml-7">
          This means I hold the necessary visas, permits, or citizenship rights that allow me to legally perform work from {country || 'my country'}. I understand that any misrepresentation may lead to the termination of my engagement.
        </p>

        <label className="flex items-start gap-2.5 cursor-pointer">
          <input type="checkbox" checked={agreeRemain} onChange={(e) => setAgreeRemain(e.target.checked)} className="w-4 h-4 rounded text-[#0A3900] mt-0.5" />
          <span className="text-sm text-gray-700">I agree to remain working from {country || 'my country'}, and to notify Duty AI in writing prior to any change. *</span>
        </label>
        <p className="text-xs text-gray-400 ml-7">
          I will not relocate my physical working location to a different country without first obtaining written approval from Duty AI. I understand that unauthorized relocation may affect my tax status, legal compliance, and contractual obligations.
        </p>
      </div>

      {/* Digital Signature */}
      <h3 className="text-base font-semibold text-gray-900 mb-4">Digital Signature</h3>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <FormField label="Full name" required value={signName} onChange={setSignName} placeholder="Your legal full name" />
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1.5 block">Date *</label>
          <input type="date" value={signDate} onChange={(e) => setSignDate(e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-[#3f8033]" />
        </div>
      </div>
      <p className="text-xs text-gray-400 mb-6">
        By completing this section, you confirm you've reviewed and agree with Duty AI's Terms and Conditions.
      </p>

      <button data-testid="work-auth-save-btn" onClick={onComplete} className="w-full py-3 bg-[#0A3900] text-white rounded-lg font-semibold text-sm hover:bg-[#1A5C0A] transition-colors">
        Save Work Authorization
      </button>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   STEP 3: GENERAL INTERVIEW (Duty AI exact)
   ═══════════════════════════════════════════════ */
const InterviewStep = ({ onComplete, job }) => {
  return (
    <div data-testid="interview-step">
      <div className="flex items-center gap-3 mb-2">
        <h2 className="text-xl font-bold text-gray-900">General Interview</h2>
        <span className="px-2 py-0.5 bg-gray-100 text-xs font-medium text-gray-600 rounded-full">20 min</span>
      </div>
      <p className="text-sm text-gray-500 mb-6">General questions about your background</p>

      {/* Camera Preview */}
      <div className="bg-gray-900 rounded-2xl aspect-video flex items-center justify-center mb-6 relative overflow-hidden">
        <div className="text-center">
          <Camera className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-400">Camera permission required</p>
          <p className="text-xs text-gray-500 mt-1">You must enable camera access before joining the AI interview.</p>
        </div>
      </div>

      <button className="text-sm text-[#0A3900] hover:text-[#0A3900] font-medium mb-6 block">Keep scrolling...</button>

      {/* Device Selectors */}
      <div className="space-y-4 mb-6">
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1.5 block">Microphone</label>
          <select className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-[#3f8033]">
            <option>Default - Microphone</option>
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1.5 block">Speaker</label>
          <select className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-[#3f8033]">
            <option>Default - Speaker</option>
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1.5 block">Camera</label>
          <select className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-[#3f8033]">
            <option>Integrated Camera</option>
          </select>
        </div>
      </div>

      {/* Test links */}
      <div className="flex items-center gap-4 mb-6">
        <button className="text-sm text-[#0A3900] hover:text-[#0A3900] font-medium flex items-center gap-1.5"><Mic className="w-4 h-4" /> Test your mic</button>
        <button className="text-sm text-[#0A3900] hover:text-[#0A3900] font-medium flex items-center gap-1.5"><Speaker className="w-4 h-4" /> Play test sound</button>
        <button className="text-sm text-[#0A3900] hover:text-[#0A3900] font-medium flex items-center gap-1.5"><RefreshCw className="w-4 h-4" /> Restart devices</button>
      </div>

      <button className="text-sm text-gray-500 hover:text-gray-700 underline mb-8">Troubleshooting help</button>

      {/* Things to know */}
      <div className="bg-gray-50 rounded-xl p-5 mb-8">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Things to know before starting</h3>
        <div className="space-y-4">
          {[
            { icon: Clock, title: 'Expect to spend ~20 minutes', desc: 'Very short interviews may not be considered complete. Please answer thoughtfully.' },
            { icon: Hand, title: 'Need assistance? Just ask', desc: 'Tell the AI if you need a question repeated or more time.' },
            { icon: RefreshCw, title: '3 of 3 interview retakes remaining', desc: 'Reserve retakes for technical issues. Each retake generates new questions.' },
            { icon: Lock, title: 'Your data is in your control', desc: 'Your responses are used only to assess your candidacy. You can delete your data at any time.' },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center shrink-0">
                <item.icon className="w-4 h-4 text-gray-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{item.title}</p>
                <p className="text-xs text-gray-500">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button data-testid="start-interview-btn" onClick={onComplete} className="w-full py-3.5 bg-[#0A3900] text-white rounded-lg font-semibold text-sm hover:bg-[#1A5C0A] transition-colors">
        Start
      </button>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   STEP 4: AVAILABILITY (Duty AI exact)
   ═══════════════════════════════════════════════ */
const AvailabilityStep = ({ onComplete }) => {
  const [startOption, setStartOption] = useState('Custom');
  const [customDays, setCustomDays] = useState('');
  const [preferredHours, setPreferredHours] = useState('');
  const [timezone, setTimezone] = useState('');
  const [schedule, setSchedule] = useState({
    Sun: { available: false, start: '', end: '' },
    Mon: { available: true, start: '9:00am', end: '5:00pm' },
    Tue: { available: true, start: '9:00am', end: '5:00pm' },
    Wed: { available: true, start: '9:00am', end: '5:00pm' },
    Thu: { available: true, start: '9:00am', end: '5:00pm' },
    Fri: { available: true, start: '9:00am', end: '5:00pm' },
    Sat: { available: false, start: '', end: '' },
  });

  const toggleDay = (day) => {
    setSchedule(prev => ({
      ...prev,
      [day]: { ...prev[day], available: !prev[day].available, start: !prev[day].available ? '9:00am' : '', end: !prev[day].available ? '5:00pm' : '' }
    }));
  };

  const timezones = [
    'America/New_York (EST)', 'America/Chicago (CST)', 'America/Denver (MST)', 'America/Los_Angeles (PST)',
    'Europe/London (GMT)', 'Europe/Berlin (CET)', 'Asia/Kolkata (IST)', 'Asia/Tokyo (JST)', 'Australia/Sydney (AEST)', 'Pacific/Auckland (NZST)'
  ];

  const dayLabels = { Sun: 'S', Mon: 'M', Tue: 'T', Wed: 'W', Thu: 'T', Fri: 'F', Sat: 'S' };

  return (
    <div data-testid="availability-step">
      <h2 className="text-xl font-bold text-gray-900 mb-2">Availability</h2>
      <p className="text-sm text-gray-500 mb-6">Please confirm your typical work hours so we can plan and match you with the right opportunities.</p>

      {/* Availability to start */}
      <div className="mb-5">
        <label className="text-sm font-medium text-gray-700 mb-1.5 block">Availability to start *</label>
        <select data-testid="start-option-select" value={startOption} onChange={(e) => setStartOption(e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-[#3f8033]">
          <option value="Immediately">Immediately</option>
          <option value="After 1 week">After 1 week</option>
          <option value="After 2 weeks">After 2 weeks</option>
          <option value="Custom">Custom</option>
        </select>
      </div>

      {startOption === 'Custom' && (
        <div className="mb-5">
          <label className="text-sm font-medium text-gray-700 mb-1.5 block">Custom start timeline</label>
          <div className="flex items-center gap-2">
            <input data-testid="custom-days-input" type="number" value={customDays} onChange={(e) => setCustomDays(e.target.value)} placeholder="Enter days until you can start" className="flex-1 h-10 px-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#3f8033]" />
            <span className="text-sm text-gray-500">days</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">Please enter a number between 1 and 99</p>
        </div>
      )}

      {/* Preferred time commitment */}
      <div className="mb-5">
        <label className="text-sm font-medium text-gray-700 mb-1.5 block">Preferred time commitment *</label>
        <input data-testid="hours-input" type="number" value={preferredHours} onChange={(e) => setPreferredHours(e.target.value)} placeholder="Ex: 40" className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#3f8033]" />
        {!preferredHours && <p className="text-xs text-red-500 mt-1">Preferred hours cannot be empty</p>}
      </div>

      {/* Timezone */}
      <div className="mb-6">
        <label className="text-sm font-medium text-gray-700 mb-1.5 block">Timezone *</label>
        <select data-testid="timezone-select" value={timezone} onChange={(e) => setTimezone(e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-[#3f8033]">
          <option value="">Select timezone</option>
          {timezones.map(tz => <option key={tz} value={tz}>{tz}</option>)}
        </select>
      </div>

      {/* Working hours */}
      <div className="mb-6">
        <label className="text-sm font-medium text-gray-700 mb-1 block">Working hours</label>
        <p className="text-xs text-gray-400 mb-4">Select when you are typically available to work</p>
        <div className="space-y-2">
          {Object.entries(schedule).map(([day, data]) => (
            <div key={day} className="flex items-center gap-3 h-10">
              <span className="w-6 text-center text-sm font-semibold text-gray-700">{dayLabels[day]}</span>
              {data.available ? (
                <>
                  <input type="text" value={data.start} onChange={(e) => setSchedule(prev => ({ ...prev, [day]: { ...prev[day], start: e.target.value } }))} className="w-24 h-9 px-2 border border-gray-200 rounded-lg text-sm text-center focus:outline-none focus:border-[#3f8033]" />
                  <span className="text-gray-400">-</span>
                  <input type="text" value={data.end} onChange={(e) => setSchedule(prev => ({ ...prev, [day]: { ...prev[day], end: e.target.value } }))} className="w-24 h-9 px-2 border border-gray-200 rounded-lg text-sm text-center focus:outline-none focus:border-[#3f8033]" />
                  <button onClick={() => setSchedule(prev => ({ ...prev, [day]: { available: false, start: '', end: '' } }))} className="p-1 text-gray-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                  <button onClick={() => {}} className="p-1 text-gray-400 hover:text-gray-600"><Eye className="w-4 h-4" /></button>
                </>
              ) : (
                <>
                  <span className="text-sm text-gray-400 flex-1">Unavailable</span>
                  <button onClick={() => toggleDay(day)} className="p-1 text-gray-400 hover:text-gray-600"><EyeOff className="w-4 h-4" /></button>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Date-specific hours */}
      <div className="mb-6">
        <label className="text-sm font-medium text-gray-700 mb-1 block">Date-specific hours</label>
        <p className="text-xs text-gray-400 mb-3">Specify date-based exceptions to your weekly availability</p>
        <button className="text-sm text-[#0A3900] hover:text-[#0A3900] font-medium flex items-center gap-1.5 mb-2">
          <Plus className="w-4 h-4" /> Add exceptions
        </button>
        <p className="text-xs text-gray-400">No active exceptions</p>
      </div>

      <p className="text-xs text-gray-400 mb-6">Please confirm your availability to proceed. You'll be able to change it later in settings.</p>

      <button data-testid="availability-submit-btn" onClick={onComplete} className="w-full py-3 bg-[#0A3900] text-white rounded-lg font-semibold text-sm hover:bg-[#1A5C0A] transition-colors">
        Submit
      </button>
    </div>
  );
};

/* ═══════════ Shared UI Components ═══════════ */
const FormField = ({ label, value, onChange, placeholder, required, type = 'text' }) => (
  <div>
    {label && <label className="text-xs font-medium text-gray-600 mb-1 block">{label}{required && ' *'}</label>}
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#3f8033]" />
  </div>
);

const YearSelect = ({ label, value, onChange, years }) => (
  <div>
    <label className="text-xs font-medium text-gray-600 mb-1 block">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-[#3f8033]">
      <option value="">Select year</option>
      {years.map(y => <option key={y} value={y}>{y}</option>)}
    </select>
  </div>
);

const SectionTitle = ({ title }) => <h3 className="text-base font-semibold text-gray-900 mt-8 mb-3 pb-2 border-b border-gray-100">{title}</h3>;

const AddBtn = ({ label, onClick }) => (
  <button onClick={onClick} className="flex items-center gap-1.5 text-sm text-[#0A3900] hover:text-[#0A3900] font-medium mb-4">
    <Plus className="w-4 h-4" /> {label}
  </button>
);

const RemoveBtn = ({ onClick }) => (
  <button onClick={onClick} className="absolute top-3 right-3 flex items-center gap-1 text-xs text-gray-400 hover:text-red-500">
    <X className="w-3.5 h-3.5" /> Remove
  </button>
);

export default ApplicationFlowPage;
